import { IpcRendererEvent } from 'electron'
import { CheckInRoleType, MatchAPI } from '@/api/match'
import { isElectron } from '@/utils/electron'
import matchStore from '@/modules/match/match.store'
import userStore from '@/modules/auth/auth.store'
import integrationStore from '@/modules/integration/integration.store'
import store from '@/store'
import { getModule } from 'vuex-module-decorators'
import { GetLocalPlayerResponse } from '@main/integrations/lcu/api/player'
import { EndOfGameResult } from '@/modules/integration/types/EndOfGameResult'
import ServerError from '@/utils/server_error'
import { AllLiveGameData } from '@main/integrations/lcu/liveclient/types'
import { getScreenSources, ScreenSourceType } from '../screencapture'
import { env } from 'process'

// increase this value for shitty computer
const INTEGRATION_RETRY_INTERVAL = 10 * 1000

export default class LeagueIntegration {
  private static retryTimer: NodeJS.Timeout | null = null
  private static matchGameId: string | null = null

  /**
   * Initialize integration. It should be called on app startup.
   */
  static init() {
    if (!isElectron()) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '[INTEGRATION] Game integration disabled in non-desktop-client mode.'
        )
      }
      return
    }

    // listen global events here
    window.ipcRenderer.on('lol:game-result', LeagueIntegration.onGameResult)
    window.ipcRenderer.on(
      'lol:game-result-aram',
      LeagueIntegration.onGameResultARAM
    )
  }

  /**
   * Start LOL integration session
   * @param matchGameId
   */
  static async start(matchGameId: string) {
    if (!isElectron()) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '[INTEGRATION] Game integration disabled in non-desktop-client mode.'
        )
      }
      return
    }

    if (LeagueIntegration.matchGameId) {
      console.log('Already started integration for LOL')
      return
    }

    LeagueIntegration.matchGameId = matchGameId
    LeagueIntegration.subscribeEvents()

    await window.ipcRenderer.send('game-integration-start', {
      game: 'lol',
      matchGameId
    })
  }

  /**
   * Stop everything immediately
   */
  static async stop() {
    if (!isElectron()) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '[INTEGRATION] Game integration disabled in non-desktop-client mode.'
        )
      }
      return
    }

    if (LeagueIntegration.retryTimer) {
      clearInterval(LeagueIntegration.retryTimer)
      LeagueIntegration.retryTimer = null
    }

    await window.ipcRenderer.send('game-integration-stop', {
      game: 'lol'
    })

    LeagueIntegration.unsubscribeEvents()
    LeagueIntegration.matchGameId = null
  }

  private static async onGameResult(
    event: IpcRendererEvent,
    data: {
      matchGameId: string
      stats: EndOfGameResult
      isWinner: boolean
      screenshot: Uint8Array
    }
  ) {
    console.log('lol:game-result - matchGameId = ', data.matchGameId)

    const statsBlob = new Blob([JSON.stringify(data.stats, null, 2)], {
      type: 'application/json'
    })

    const screenshotBlob = new Blob([data.screenshot], {
      type: 'image/jpeg'
    })

    const integrationModule = getModule(integrationStore, store)
    await integrationModule.setResult({
      result: data.stats,
      gameMode: data.stats.gameMode,
      isWinner: data.isWinner,
      isSoloGame:
        data.stats.teams.filter(team => team.players.length <= 1).length === 2
    })
    await integrationModule.setEvidenceBlobs([screenshotBlob])

    // Submit Match data JSON
    try {
      await MatchAPI.submitMatchGameEvidence(data.matchGameId, statsBlob, true)
    } catch (err) {
      console.error('Unable to submit game integration data (stats)', err)
    }

    // Submit Match Screenshot
    try {
      await MatchAPI.submitMatchGameEvidence(
        data.matchGameId,
        screenshotBlob,
        true
      )
    } catch (err) {
      console.error('Unable to submit game integration data (screenshot)', err)
    }

    if (window.ipcRenderer) {
      window.ipcRenderer.send('focus-window')
    }
  }

  private static async onGameResultARAM(
    event: IpcRendererEvent,
    data: {
      matchGameId: string
      isWinner: boolean
      result: AllLiveGameData
    }
  ) {
    console.log('lol:game-result-aram - matchGameId = ', data.matchGameId)

    const integrationModule = getModule(integrationStore, store)
    const sources = await getScreenSources(ScreenSourceType.SOURCE_SCREEN)
    for (const source of sources!) {
      console.log('[Capturer] Retrieved screenshot from', source.name)
      const screenshotBlob = new Blob([source.thumbnail.toPNG()], {
        type: 'image/png'
      })
      await integrationModule.addEvidenceBlob(screenshotBlob)
    }

    const statsBlob = new Blob([JSON.stringify(data.result, null, 2)], {
      type: 'application/json'
    })
    try {
      await MatchAPI.submitMatchGameEvidence(data.matchGameId, statsBlob, true)
    } catch (err) {
      console.error('Unable to submit game integration data (stats)', err)
    }

    const isSoloGame = data.result.allPlayers.length <= 2
    await integrationModule.setResult({
      result: data.result,
      isWinner: data.isWinner,
      gameMode: data.result.gameData.gameMode,
      isSoloGame: isSoloGame
    })

    if (window.ipcRenderer) {
      setTimeout(() => {
        window.ipcRenderer.send('focus-window')
      }, 60 * 1000)
    }
  }

  private static async onError(event: IpcRendererEvent) {
    console.log(
      'LcuConnector has an unrecoverable error occured. Fallback to manual submit result'
    )

    const integrationModule = getModule(integrationStore, store)
    await integrationModule.updateConnectionState(false)

    // meanwhile, retry in the background. stop previously retry timer first just in case
    if (LeagueIntegration.retryTimer) {
      clearInterval(LeagueIntegration.retryTimer)
      LeagueIntegration.retryTimer = null
    }

    LeagueIntegration.retryTimer = setInterval(
      LeagueIntegration.onRetryTimer,
      INTEGRATION_RETRY_INTERVAL
    )
  }

  private static async onReady(event: IpcRendererEvent) {
    console.log('Game is connected. Tell the user it is ready to go')

    const integrationModule = getModule(integrationStore, store)
    await integrationModule.updateConnectionState(true, this.matchGameId)
    // stop retrying if we are
    if (LeagueIntegration.retryTimer) {
      clearInterval(LeagueIntegration.retryTimer)
      LeagueIntegration.retryTimer = null
    }

    const matchModule = getModule(matchStore, store)
    const userModule = getModule(userStore, store)

    if (
      matchModule.matchGame &&
      matchModule.match &&
      matchModule.match.leagueMap !== null
    ) {
      // check if lobby hasn't been created
      if (matchModule.match.leagueLobby) {
        // find which team it is
        const checkIn = matchModule.matchGame!.checkIns.find(
          checkIn => checkIn.member.memberId === userModule.userId
        )

        // find which team it is
        const matchTeamId = checkIn!.matchTeamId
        const teamOneMatchTeamId = matchModule.matchGame!.teams[0].matchTeamId

        // do not join for the one who is creating custom game
        if (
          teamOneMatchTeamId === matchTeamId &&
          checkIn!.role === CheckInRoleType.Captain
        ) {
          return
        }

        await integrationModule.joinCustomGame({
          lobby: matchModule.match.leagueLobby,
          team: teamOneMatchTeamId === matchTeamId ? 'one' : 'two'
        })
      } else {
        // get team 0 captain
        const team0Id = matchModule.matchGame.teams[0].matchTeamId
        const captainMemberId = matchModule.matchGame.checkIns.find(
          checkIn =>
            checkIn.matchTeamId === team0Id &&
            checkIn.role === CheckInRoleType.Captain
        )!.member.memberId

        // check if im team 0 leader
        if (userModule.memberId === captainMemberId) {
          console.log('Creating custom game.')

          // let's create match
          const password = (
            Math.floor(Math.random() * 999999) + 100000
          ).toString()
          const name =
            '[VSARENA] ' +
            matchModule.match.matchId.substr(0, 6) +
            '-' +
            Math.ceil(Math.random() * 100000)

          let customGameId = ''
          let retries = 10

          while (retries >= 0 && customGameId === '') {
            customGameId = await window.ipcRenderer.invoke(
              'lol:create-custom-game',
              {
                lobbyName: name,
                lobbyPassword: password,
                teamSize: 5,
                mapId: matchModule.match.leagueMap,
                mutatorId: matchModule.match.leaguePickMode
              }
            )

            await new Promise(resolve => {
              setTimeout(resolve, 1000)
            })

            retries--
          }

          if (customGameId === '') {
            await integrationModule.setJoiningCustomGameFailed(true)
            return
          }

          // send the custom game ID to server
          try {
            await MatchAPI.updateLeagueLobby(
              matchModule.match.matchId,
              customGameId,
              name,
              password
            )

            console.log(
              'League lobby updated to backend.',
              customGameId,
              name,
              password
            )
          } catch (err) {
            console.error('Unable to update league lobby to backend.', err)
          }
        }
      }
    }
  }

  private static async onClose(event: IpcRendererEvent) {
    const integrationModule = getModule(integrationStore, store)
    await integrationModule.updateConnectionState(false)
  }

  private static async onRetry(
    event: IpcRendererEvent,
    args: { retries: number }
  ) {
    console.log('Game conenction lost. Retrying...', args.retries)
    const integrationModule = getModule(integrationStore, store)
    await integrationModule.updateConnectionState(false)
  }

  private static async onRetryTimer() {
    console.log('Retrying integration session...')
    await window.ipcRenderer.send('game-integration-start', {
      game: 'lol',
      matchGameId: LeagueIntegration.matchGameId
    })
  }

  private static async onUserInfoReceived(
    event: IpcRendererEvent,
    args: GetLocalPlayerResponse
  ) {
    console.log('Received LCU local session user info')
    const integrationModule = getModule(integrationStore, store)
    await integrationModule.updateUserInfo(args)
  }

  private static subscribeEvents() {
    window.ipcRenderer.on('lol:user', LeagueIntegration.onUserInfoReceived)

    // connection state
    window.ipcRenderer.on('lol:ready', LeagueIntegration.onReady)
    window.ipcRenderer.on('lol:error', LeagueIntegration.onError)
    window.ipcRenderer.on('lol:close', LeagueIntegration.onClose)
    window.ipcRenderer.on('lol:retry', LeagueIntegration.onRetry)
  }

  private static unsubscribeEvents() {
    window.ipcRenderer.off('lol:user', LeagueIntegration.onUserInfoReceived)

    // connection state
    window.ipcRenderer.off('lol:ready', LeagueIntegration.onReady)
    window.ipcRenderer.off('lol:error', LeagueIntegration.onError)
    window.ipcRenderer.off('lol:close', LeagueIntegration.onClose)
    window.ipcRenderer.off('lol:retry', LeagueIntegration.onRetry)
  }
}
